<template>
  <label :class="[$options.name, 'tw:gap-2']" :data-e-ga-id="uniqueId">
    <input
      :checked="checked"
      class="input"
      type="checkbox"
      v-bind="$attrs"
      :disabled="disabled"
      @change="handleChange"
    />
    <span :class="[`${$options.name}__switch`, 'switch', isAi ? 'ai' : '']"></span>
    <span class="tw:flex tw:flex-col tw:gap-1">
      <div class="tw:flex tw:gap-1 tw:items-center">
        <slot />
        <span v-if="label" class="tw:body-1" :class="{ 'tw:text-violet-500': isAi }">{{ label }}</span>
      </div>
      <span v-if="helper" class="tw:body-2 tw:text-default-700">{{ helper }}</span>
    </span>
  </label>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneToggle",
  props: {
    /**
     * Label that is displayed next to the toggle
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Text that is displayed next to the toggle and below the label
     */
    helper: {
      type: String,
      default: "",
    },
    /**
     * Whether the toggle is disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the toggle is :checked. Required.
     */
    checked: {
      type: Boolean,
      required: true,
    },
    /**
     * Whether the toggle is controlled by the parent component
     * controlled: false: The component is "uncontrolled," managing its internal state.
     * controlled: true: The component is "controlled," and its value is managed by the parent through props.
     */
    controlled: {
      type: Boolean,
      default: false,
    },
    uniqueId: {
      type: String,
      default: "",
    },
    isAi: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    /**
     * Emits when the toggle is clicked with the value of the toggle
     */
    click: (value: boolean) => value,
  },
  setup(props, { emit }) {
    const internalChecked = computed(() => {
      return props.checked;
    });

    function handleChange(event: Event) {
      const target = event.target as HTMLInputElement;
      emit("click", target.checked);
      gtagUniversalSend({ component_id: props.uniqueId + "-" + target.checked });
      if (props.controlled) {
        target.checked = internalChecked.value;
      }
    }

    return {
      internalChecked,
      handleChange,
    };
  },
});
</script>

<style>
@reference "vue-app/assets/scss/main.css";

.EdroneToggle {
  cursor: pointer;
  display: flex;
  width: 100%;
}

/* Visually hide the checkbox input */
.EdroneToggle .input {
  border-width: 0;
  clip: rect(0, 0, 0, 0);
  height: 1px;
  margin: -1px;
  display: none;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.EdroneToggle .input:hover + .switch {
  @apply tw:bg-default-200;
}

.EdroneToggle .input:checked + .switch {
  @apply tw:bg-blue-500;
}

.EdroneToggle .input:checked + .switch:before {
  /* Move the inner circle to the right */
  transform: translateX(14px);
}

.EdroneToggle .input:checked + .ai {
  @apply tw:!bg-violet-500;
}

.EdroneToggle .input:checked:hover + .switch {
  @apply tw:bg-blue-600;
}

.EdroneToggle .input:checked:hover + .ai {
  @apply tw:!bg-violet-600;
}

.EdroneToggle .input:checked:disabled + .switch {
  @apply tw:bg-blue-100;
}

.EdroneToggle .input:checked:disabled + .switch:before {
  @apply tw:bg-blue-000 tw:border-blue-000;
}

.EdroneToggle .input:checked:disabled + .ai {
  @apply tw:!bg-violet-100;
}

.EdroneToggle .input:disabled + .switch {
  @apply tw:bg-default-100;
}

.EdroneToggle .input:disabled + .switch:before {
  @apply tw:bg-default-000 tw:border-default-000;
}

.EdroneToggle__switch {
  /* Vertically center the inner circle */
  display: flex;
  align-items: center;
  position: relative;
  height: 20px;
  width: 34px;
  border-radius: 6px;
  @apply tw:bg-default-100;
  /* In case the label gets really long, the toggle shouldn't shrink. */
  flex-shrink: 0;
  transition: background-color 0.25s ease-in-out;
}

.EdroneToggle__switch:before {
  content: "";
  position: absolute;
  left: 2px;
  height: 16px;
  width: 16px;
  border-radius: 4px;
  background-color: white;
  transition: transform 0.375s ease-in-out;
}
</style>
